import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { MdArrowBack, MdOutlineMarkEmailRead } from "react-icons/md";
import axios from "../config/axiosInstance";
import { EMAIL } from "../config/apiConfig";
import {
  Badge,
  Card,
  EmptyState,
  ErrorBlock,
  StatTile,
} from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonKpiStrip,
  SkeletonTable,
} from "../components/crm/Skeletons";
import { formatDate, formatNumber, formatPercent } from "../components/crm/crmHelpers";
import ClientLink from "../components/crm/ClientLink";

/*
  Relatório de uma campanha.

  Mostra a fila junto com o resultado, e não só o resultado: enquanto o worker
  trabalha, "0% de abertura" com 2.000 na fila e "0% de abertura" com tudo
  enviado significam coisas opostas, e um relatório que esconde a fila faria
  alguém concluir que a campanha fracassou quando ela mal começou.
*/

const DELIVERY_STATUS = {
  QUEUED: { label: "Na fila", tone: "info" },
  SENT: { label: "Enviado", tone: "success" },
  FAILED: { label: "Falhou", tone: "danger" },
  SKIPPED: { label: "Cancelado", tone: "neutral" },
};

const EmailReportPage = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const { data, isLoading, error, refetch } = useQuery(
    ["email", "report", id, page, statusFilter],
    async () => {
      const params = new URLSearchParams({ page: String(page) });
      if (statusFilter) params.set("status", statusFilter);

      const { data: response } = await axios.get(
        `${EMAIL.CAMPAIGN}/${id}/report?${params.toString()}`
      );
      return response.data;
    },
    { keepPreviousData: true, refetchInterval: 30_000 }
  );

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonHeader withAction={false} />
        <SkeletonKpiStrip count={5} withIcon={false} />
        <SkeletonTable rows={8} cols={5} withAvatar={false} />
      </div>
    );
  }
  if (error) {
    return (
      <ErrorBlock
        message={error.response?.data?.message || "Não foi possível carregar o relatório."}
        onRetry={refetch}
      />
    );
  }

  const { campaign, breakdown, deliveries, totalPages, totalCount } = data;
  const emFila = breakdown.QUEUED || 0;

  return (
    <div className="space-y-5">
      <header className="flex items-start gap-3">
        <Link
          to="/emails"
          className="mt-1 shrink-0 text-slate-400 transition hover:text-pink-600"
          title="Voltar"
        >
          <MdArrowBack size={20} />
        </Link>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{campaign.name}</h1>
          <p className="text-sm text-slate-500">{campaign.subject}</p>
          <p className="mt-1 text-xs text-slate-400">
            {campaign.startedAt ? `Iniciada em ${formatDate(campaign.startedAt)}` : "Não iniciada"}
            {campaign.finishedAt ? ` · concluída em ${formatDate(campaign.finishedAt)}` : ""}
          </p>
        </div>
      </header>

      {emFila > 0 ? (
        <p className="rounded-xl bg-sky-50 px-4 py-2.5 text-xs text-sky-900 ring-1 ring-inset ring-sky-200">
          <strong>{formatNumber(emFila)}</strong> e-mail(s) ainda na fila. O
          envio é espalhado ao longo das horas para não derrubar a entrega do
          domínio — as taxas abaixo só ficam definitivas quando a fila zerar.
        </p>
      ) : null}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile
          label="Destinatários"
          value={formatNumber(campaign.totalRecipients)}
          hint={emFila ? `${formatNumber(emFila)} na fila` : "fila concluída"}
          tone="neutral"
        />
        <StatTile
          label="Entregues"
          value={formatNumber(campaign.sentCount)}
          hint={`${formatNumber(campaign.failedCount)} falha(s)`}
          tone={campaign.failedCount ? "warning" : "success"}
        />
        <StatTile
          label="Abertura"
          value={formatPercent(campaign.openRate)}
          hint={`${formatNumber(campaign.openedCount)} pessoa(s)`}
          tone={campaign.openRate >= 20 ? "success" : "neutral"}
        />
        <StatTile
          label="Clique"
          value={formatPercent(campaign.clickRate)}
          hint={`${formatNumber(campaign.clickedCount)} pessoa(s)`}
          tone={campaign.clickRate >= 2 ? "success" : "neutral"}
        />
        <StatTile
          label="Saíram da lista"
          value={formatPercent(campaign.unsubscribeRate)}
          hint={`${formatNumber(campaign.unsubscribedCount)} pessoa(s)`}
          /* Acima de 2% o problema é o conteúdo ou o segmento, não o e-mail. */
          tone={campaign.unsubscribeRate > 2 ? "danger" : "neutral"}
        />
      </section>

      <div className="flex flex-wrap gap-1.5">
        {["", "SENT", "QUEUED", "FAILED", "SKIPPED"].map((value) => (
          <button
            key={value || "ALL"}
            type="button"
            onClick={() => {
              setStatusFilter(value);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset transition ${
              statusFilter === value
                ? "bg-slate-900 text-white ring-slate-900"
                : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-300"
            }`}
          >
            {value ? DELIVERY_STATUS[value].label : "Todos"}
            {value ? ` ${formatNumber(breakdown[value] || 0)}` : ""}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        {!deliveries.length ? (
          <EmptyState
            icon={MdOutlineMarkEmailRead}
            title="Nenhum envio neste recorte"
            description="Troque o filtro acima."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">Destinatário</th>
                  <th className="px-4 py-2.5">Situação</th>
                  <th className="px-4 py-2.5">Abriu</th>
                  <th className="px-4 py-2.5">Clicou</th>
                  <th className="px-4 py-2.5">Saiu</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deliveries.map((delivery) => {
                  const meta = DELIVERY_STATUS[delivery.status];
                  return (
                    <tr key={delivery.id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-2.5">
                        <ClientLink
                          to={`/clientes/${delivery.companyId}`}
                          className="block font-medium text-slate-800 hover:text-pink-700"
                        >
                          {delivery.name || delivery.email}
                        </ClientLink>
                        <span className="block truncate text-[11px] text-slate-500">
                          {delivery.email}
                          {delivery.error ? ` · ${delivery.error}` : ""}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge tone={meta.tone}>{meta.label}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        {delivery.openedAt ? formatDate(delivery.openedAt) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        {delivery.clickedAt ? formatDate(delivery.clickedAt) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600">
                        {delivery.unsubscribedAt
                          ? formatDate(delivery.unsubscribedAt)
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <span className="text-xs text-slate-500">
              {formatNumber(totalCount)} envio(s) · página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
                className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
              >
                anterior
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 disabled:opacity-40"
              >
                próxima
              </button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default EmailReportPage;
